/**
 * Section Header Animation
 * Creates expanding border animation for section headers when they come into view
 */

class SectionHeaderAnimation {
  constructor() {
    this.animatedHeaders = new Set()
    this.init()
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAnimation())
    } else {
      this.setupAnimation()
    }
  }

  setupAnimation() {
    // Find all section headers
    const sectionHeaders = document.querySelectorAll('.section-header')

    if (sectionHeaders.length === 0) return

    // Create intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.animateHeader(entry.target)
            // Don't unobserve - keep watching for re-renders
          }
        })
      },
      {
        threshold: 0.3, // Trigger when 30% of the element is visible
        rootMargin: '0px 0px -50px 0px', // Start animation slightly before element is fully visible
      }
    )

    // Observe all section headers
    sectionHeaders.forEach((header) => {
      observer.observe(header)
    })

    // Set up a MutationObserver to watch for class changes
    this.setupMutationObserver()
  }

  /**
   * @param {Element} header
   */
  animateHeader(header) {
    // Add animated state to trigger border expansion
    header.classList.add('section-header--animated')
    this.animatedHeaders.add(header)
  }

  setupMutationObserver() {
    // Watch for changes to section headers that might remove our animation class
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target
          if (
            target instanceof Element &&
            target.classList.contains('section-header') &&
            this.animatedHeaders.has(target) &&
            !target.classList.contains('section-header--animated')
          ) {
            // Re-apply the animation class if it was removed
            target.classList.add('section-header--animated')
          }
        }
      })
    })

    // Observe all section headers for class changes
    const sectionHeaders = document.querySelectorAll('.section-header')
    sectionHeaders.forEach((header) => {
      mutationObserver.observe(header, {
        attributes: true,
        attributeFilter: ['class'],
      })
    })
  }
}

// Initialize the animation
new SectionHeaderAnimation()
